import actions from './actions';
import { createReducer } from '../../../../../../helpers/reducer';
import { tableConfig } from '../../../../../../settings';

const initState = {
  agencies: [],
  pool: null,
  filteredTotal: 0,
  filters: [],
  loading: false,
  total: 0,
  page: 1,
  pageSize: tableConfig.pageSize,
  sortableColumns: [],
  selectedTalents: [],
  excludedTalents: [],
  isBulkSelected: false,
  bulkActions: [],
  bulk_actions_enabled: false,
  isBulkInviteSentAlertVisible: false,
  availableActions: [],
  availableStatus: [],
  isAvailableActionsLoading: false,
  bulkByField: undefined,
};

function sortFilters(filters) {
  const accreditations = filters.find(filter => filter.name === 'accreditations');
  const tier = filters.find(filter => filter.name === 'tier');
  const workedJob = filters.find(filter => filter.name === 'worker_job_previous');
  const gender = filters.find(filter => filter.name === 'gender');
  const age = filters.find(filter => filter.name === 'age');
  const rating = filters.find(filter => filter.name === 'worker_score');
  const score = filters.find(filter => filter.name === 'ma_score');
  const status = filters.find(filter => filter.name === 'status');

  return [accreditations, tier, workedJob, gender, age, rating, score, status].filter(filter => filter);
}

function onLoad(state) {
  return {
    ...state,
    loading: true,
    isEdit: false,
  };
}

function onLoadSuccess(state, { payload }) {
  const {
    data,
    total,
    filteredTotal,
    filters,
    page,
    pageSize,
    isLoadMore,
    bulkActions,
    bulk_actions_enabled,
    sortableColumns,
  } = payload;

  return {
    ...state,
    pool: [...((isLoadMore && state?.pool) || []), ...data],
    filters: sortFilters(filters),
    filteredTotal,
    loading: false,
    total,
    page,
    pageSize,
    bulkActions,
    bulk_actions_enabled,
    sortableColumns,
  };
}

function onLoadMyTeamData(state) {
  return {
    ...state,
    loading: true,
  };
}

function onLoadMyTeamSuccess(state, { payload }) {
  const { data, total, filteredTotal, filters } = payload;

  return {
    ...state,
    pool: data,
    filteredTotal,
    filters,
    loading: false,
    total,
  };
}

function onLoadAgenciesData(state) {
  return {
    ...state,
    loading: true,
  };
}

function onLoadAgenciesSuccess(state, { payload }) {
  const { data, total, filteredTotal, filters } = payload;

  return {
    ...state,
    agencies: data,
    filteredTotal,
    filters,
    loading: false,
    total,
  };
}

function onLoadAfterAction(state, { payload }) {
  const { data } = payload;
  const { pool } = state;

  if (!pool) {
    return state;
  }

  const index = pool.findIndex(item => item.id === data.id);

  pool[index] = {
    ...pool[index],
    ...data,
    isDateBold: true,
  };

  return { ...state, pool: [...pool] };
}

function getUpdatedItems(talents, bulkByField, item, value, type) {
  const included = talents.includes(item.id);

  if (included) {
    return talents.filter(talentId => talentId !== item.id);
  }

  const isBulkFieldItem = bulkByField && bulkByField?.value?.name === item[bulkByField?.field]?.name;

  switch (type) {
    case 'selected':
      return !isBulkFieldItem && value ? [...talents, item.id] : talents;
    case 'excluded':
      return isBulkFieldItem && !value ? [...talents, item.id] : talents;
    default:
      return talents;
  }
}

function onChangeChecked(state, { payload }) {
  const { item, value } = payload;
  const { selectedTalents, isBulkSelected, bulkByField, excludedTalents } = state;

  if (item === 'all') {
    return {
      ...state,
      isBulkSelected: !isBulkSelected,
      selectedTalents: [],
      excludedTalents: [],
      bulkByField: undefined,
      loading: false,
    };
  }

  if (isBulkSelected) {
    const updatedExcludedTalents = value
      ? excludedTalents.filter(talentId => talentId !== item.id)
      : [...excludedTalents, item.id];

    return { ...state, excludedTalents: updatedExcludedTalents };
  }

  return {
    ...state,
    selectedTalents: getUpdatedItems(selectedTalents, bulkByField, item, value, 'selected'),
    excludedTalents: getUpdatedItems(excludedTalents, bulkByField, item, value, 'excluded'),
    loading: false,
  };
}

function clearSelection(state) {
  return {
    ...state,
    isBulkSelected: false,
    selectedTalents: [],
    excludedTalents: [],
    bulkByField: undefined,
    loading: false,
  };
}

function onClearStore() {
  return { ...initState };
}

function toggleBulkInviteAlertVisibility(state, { payload }) {
  return {
    ...state,
    isBulkInviteSentAlertVisible: payload?.visibility || !state.isBulkInviteSentAlertVisible,
  };
}

function onLoadAvailableAction(state) {
  return {
    ...state,
    isAvailableActionsLoading: true,
  };
}

function onLoadAvailableActionSuccess(state, { payload }) {
  const { availableActions, availableStatuses } = payload;

  return {
    ...state,
    isAvailableActionsLoading: false,
    availableActions,
    availableStatuses,
  };
}

function onSelectBulkByField(state, { payload }) {
  return {
    ...state,
    bulkByField:
      state.bulkByField?.field === payload?.field && state.bulkByField?.value.id === payload?.value?.id
        ? undefined
        : payload,
    isBulkSelected: false,
    selectedTalents: [],
    excludedTalents: [],
  };
}

function onInviteAgencySuccess(state, { payload }) {
  const agencies = state.agencies.map(agency => {
    if (agency.id === payload.id) {
      return payload;
    }

    return { ...agency };
  });

  return { ...state, agencies };
}

export default createReducer(initState, {
  [actions.LOAD]: onLoad,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.LOAD_MY_TEAM_REQUEST]: onLoadMyTeamData,
  [actions.LOAD_MY_TEAM_SUCCESS]: onLoadMyTeamSuccess,
  [actions.LOAD_AGENCIES_REQUEST]: onLoadAgenciesData,
  [actions.LOAD_AGENCIES_SUCCESS]: onLoadAgenciesSuccess,
  [actions.LOAD_AFTER_ACTION]: onLoadAfterAction,
  [actions.INVITE_AGENCY_SUCCESS]: onInviteAgencySuccess,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.CHANGE_CHECKED]: onChangeChecked,
  [actions.CLEAR_SELECTION]: clearSelection,
  [actions.TOGGLE_BULK_INVITE_ALERT_VISIBILITY]: toggleBulkInviteAlertVisibility,
  [actions.LOAD_AVAILABLE_ACTION_REQUEST]: onLoadAvailableAction,
  [actions.LOAD_AVAILABLE_ACTION_SUCCESS]: onLoadAvailableActionSuccess,
  [actions.SELECT_BULK_BY_STATUS]: onSelectBulkByField,
});
